import { useMemo } from "react";

type InputValue = string | number | null | undefined;

/**
 * useThousandsSeparator
 *
 * یک Hook سفارشی React برای قالب‌بندی اعداد با جداکننده هزارگان به صورت فارسی.
 *
 * @param input عدد یا رشته ورودی
 * @returns رشته قالب‌بندی‌شده با جداکننده هزارگان یا رشته خالی در صورت نامعتبر بودن ورودی
 *
 * @example
 * ```ts
 * const formatted = useThousandsSeparator(1234567);
 * console.log(formatted); // "۱,۲۳۴,۵۶۷"
 * ```
 */
function useThousandsSeparator(input: InputValue): string {
  const formatted = useMemo(() => {
    if (!input) return "۰";
    const parsed = Number(input);
    if (isNaN(parsed)) return "";
    return parsed.toLocaleString("fa-IR");
  }, [input]);

  return formatted;
}

export default useThousandsSeparator;

/**
 * numberSeparator
 *
 * قالب‌بندی یک عدد با جداکننده هزارگان به صورت فارسی (موازی با Hook).
 *
 * @param input عدد یا رشته ورودی
 * @returns رشته قالب‌بندی‌شده یا رشته خالی در صورت نامعتبر بودن ورودی
 *
 * @example
 * ```ts
 * console.log(numberSeparator(1234567)); // "۱,۲۳۴,۵۶۷"
 * ```
 */
export function numberSeparator(input: InputValue): string {
  if (!input) return "۰";
  const parsed = Number(input);
  if (isNaN(parsed)) return "";
  return parsed.toLocaleString("fa-IR");
}

/**
 * toPersianNumber
 *
 * تبدیل اعداد انگلیسی موجود در رشته یا عدد به اعداد فارسی.
 *
 * @param str عدد یا رشته ورودی
 * @returns رشته‌ای با اعداد فارسی
 *
 * @example
 * ```ts
 * console.log(toPersianNumber(123456)); // "۱۲۳۴۵۶"
 * ```
 */
export function toPersianNumber(str: string | number | undefined): string {
  if (!str) return "۰";
  return str
    .toString()
    .replace(/,/g, "")
    .replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[+d]);
}

/**
 * toEnglishNumber
 *
 * تبدیل اعداد فارسی موجود در رشته به اعداد انگلیسی.
 *
 * @param str رشته حاوی اعداد فارسی
 * @returns رشته‌ای با اعداد انگلیسی
 *
 * @example
 * ```ts
 * console.log(toEnglishNumber("۱۲۳۴۵۶")); // "123456"
 * ```
 */
export function toEnglishNumber(str: string): string {
  if (!str) return "-";
  return str.replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString());
}
